import React from "react";
import { Tooltip } from "antd";

import type { Project } from "../../../../core/data/redux/projects/types";
import { PROJECT_PRIORITY_OPTIONS } from "../../constants/projects.constants";

interface ProjectStatsHeaderProps {
  project: Project | null;
}

const PRIORITY_GRADIENT: Record<number, string> = {
  2: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
  1: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
  0: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)",
};

const ProjectStatsHeader: React.FC<ProjectStatsHeaderProps> = ({ project }) => {
  if (!project) return null;
  const stats = project.stats || {
    total: 0,
    by_status_category: { TODO: 0, IN_PROGRESS: 0, DONE: 0 },
  };
  const total = stats.total || 0;
  const done = stats.by_status_category?.DONE || 0;
  const inProgress = stats.by_status_category?.IN_PROGRESS || 0;
  const todo = stats.by_status_category?.TODO || 0;
  const completionPct = total > 0 ? Math.round((done / total) * 100) : 0;
  const priorityLabel =
    PROJECT_PRIORITY_OPTIONS.find((o) => o.value === project.priority)?.label ?? "—";

  return (
    <div
      className="card mb-3 border-0 overflow-hidden"
      style={{
        borderRadius: 14,
        boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
      }}
    >
      {/* Gradient accent bar */}
      <div
        style={{
          height: 4,
          background: PRIORITY_GRADIENT[project.priority ?? 1] || PRIORITY_GRADIENT[1],
        }}
      />

      <div className="card-body bg-white" style={{ padding: "14px 20px" }}>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3 min-width-0">
            <div
              className="d-flex align-items-center justify-content-center rounded text-white fw-bold"
              style={{
                width: 48,
                height: 48,
                background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                fontSize: 18,
                borderRadius: 10,
                flexShrink: 0,
              }}
            >
              {project.key?.slice(0, 2)}
            </div>
            <div className="min-width-0">
              <div className="d-flex align-items-center gap-2 mb-1">
                <h4 className="mb-0 text-truncate" style={{ color: "#0f172a", fontWeight: 650 }}>
                  {project.name}
                </h4>
                <Tooltip title={`Key: ${project.key}`}>
                  <span className="badge badge-md badge-danger-transparent">{project.key}</span>
                </Tooltip>
              </div>
              {project.description && (
                <div
                  className="text-muted"
                  style={{
                    maxWidth: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 12.5,
                  }}
                  dangerouslySetInnerHTML={{ __html: project.description }}
                />
              )}
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <StatBlock
              icon="ti ti-list-check"
              label="Total"
              value={total}
              color="#0F172A"
              bg="#E2E8F0"
              iconColor="#334155"
            />

            <StatBlock
              icon="ti ti-clock"
              label="To Do"
              value={todo}
              color="#D97706"
              bg="#FEF3C7"
              iconColor="#F59E0B"
            />


            <StatBlock
              icon="ti ti-progress"
              label="In Progress"
              value={inProgress}
              color="#3B82F6"
              bg="#eff6ff"
              iconColor="#3B82F6"
            />
            <StatBlock
              icon="ti ti-circle-check"
              label="Done"
              value={done}
              color="#10B981"
              bg="#ecfdf5"
              iconColor="#10B981"
            />
            <StatBlock
              icon="ti ti-percentage"
              label="Progress"
              value={`${completionPct}%`}
              color="#8b5cf6"
              bg="#faf5ff"
              iconColor="#8b5cf6"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface StatBlockProps {
  icon: string;
  label: string;
  value: number | string;
  color?: string;
  bg?: string;
  iconColor?: string;
}

const StatBlock: React.FC<StatBlockProps> = ({ icon, label, value, color, bg, iconColor }) => (
  <Tooltip title={`${label}: ${value}`}>
    <div
      className="d-flex align-items-center gap-2"
      style={{
        cursor: "help",
        padding: "8px 12px",
        borderRadius: 10,
        background: bg || "#f8fafc",
        whiteSpace: "nowrap",
        transition: "transform 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ width: 32, height: 32, borderRadius: 8, background: iconColor ? iconColor + "18" : "#e2e8f0", flexShrink: 0 }}
      >
        <i className={icon} style={{ fontSize: 16, color: iconColor || "#64748b" }} />
      </div>
      <div className="d-flex flex-column justify-content-center">
        <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 0.5, lineHeight: 1 }}>
          {label}
        </div>
        <div className="fw-bold mt-1" style={{ fontSize: 16, lineHeight: 1, ...(color ? { color } : {}) }}>
          {value}
        </div>
      </div>
    </div>
  </Tooltip>
);

export default ProjectStatsHeader;
